import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Page, TextButton } from "@towersystems/roam-common-native";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import React from "react";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { OfflineBanner } from "../../../../components/OfflineBanner/OfflineBanner";
import { SaleRootParamList } from "../../../../types";
import { SearchContext, useSearch } from "../../../../utilities/search";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { useTranslation } from "../../../../utilities/i18n";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { useLoadMoreList } from "../../../../utilities/load-more-list";
import { Sale } from "@towersystems/roam-common/lib/generated-types";
import { QUERY_SALE_LIST } from "../../utils";
import { SalesResourceList } from "../../components/SaleResourceList";
import { useScheduler } from "../../../../utilities/scheduler";
import { useFocusEffect } from "@react-navigation/native";
import { SalesFilterValues } from "../../types";
import { saleFilterValuesToApiInput } from "../../common";
import { AntDesign } from "@expo/vector-icons";

export type SalesListScreenProps = NativeStackScreenProps<
  SaleRootParamList,
  "SalesListScreen"
>;

export const SalesListScreen = ({ navigation }: SalesListScreenProps) => {
  const { t } = useTranslation("sales");
  const search = useSearch();
  const online = useOnline();
  const transitionEnded = useHasTransitionEnded(navigation);
  const { filterState, watch } = useListFilterContext<SalesFilterValues>();
  const { activeFiltersCount } = filterState;
  const watchedFilterValues = watch();
  const { schedule } = useScheduler(500);

  const loadMoreList = useLoadMoreList<Sale>({
    initialLoad: false,
    query: QUERY_SALE_LIST,
    resultKey: "sales",
    sort: { updatedAt: "DESC" },
    variableFormatter: (variables) => {
      return {
        options: variables,
      };
    },
  });

  const handleLoadMore = (reset?: boolean) => {
    loadMoreList.loadMore(
      {
        filter: {
          invoiceNumber: { contains: search.query ?? undefined },
          ...saleFilterValuesToApiInput(watchedFilterValues),
        },
      },
      reset
    );
  };

  useFocusEffect(
    React.useCallback(() => {
      handleLoadMore(true);
    }, [watchedFilterValues])
  );

  React.useEffect(() => {
    if (loadMoreList.called) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query]);

  React.useEffect(() => {
    navigation.setOptions({
      headerTitle: () => {
        return (
          <TextButton
            iconStyle={{ marginRight: 0 }}
            icon={<AntDesign name="filter" size={20} />}
            onPress={() =>
              navigation.navigate("SalesListFilterRootScreen", {
                screen: "SalesListFilterScreen",
              })
            }
          >
            {t("common:filter", {
              count: activeFiltersCount,
            })}
          </TextButton>
        );
      },
    });
  }, [activeFiltersCount]);

  const items: Sale[] = loadMoreList.items || [];

  if (!online) {
    return (
      <Page title={t("name")} scrollable={false}>
        <OfflineBanner />
      </Page>
    );
  }

  if (false == transitionEnded) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />
      </Page>
    );
  }

  if (loadMoreList.loading && loadMoreList.items.length == 0) {
    return <FlatListSearchableSkeleton />;
  }

  return (
    <Page padding={0} fullWidth title={t("name")} scrollable={false}>
      <SearchContext.Provider value={search}>
        <SalesResourceList
          items={items}
          onEndReached={() => handleLoadMore(false)}
        />
      </SearchContext.Provider>
    </Page>
  );
};
